import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import SEO from '@/components/SEO';

// Restrict allowed tags/attrs — admin-authored content still passes through DOMPurify
// so a compromised admin account or bad markdown can't execute scripts on customers.
const SANITIZE_OPTS = {
  ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'p', 'ul', 'ol', 'li', 'strong', 'em', 'u',
    'blockquote', 'a', 'hr', 'br', 'div', 'span', 'code', 'pre'],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class'],
  ALLOW_DATA_ATTR: false,
};
const clean = (html) => DOMPurify.sanitize(html || '', SANITIZE_OPTS);

const renderMarkdown = (text) => {
  if (!text) return null;
  // If content looks like HTML (from WYSIWYG editor), sanitize before rendering.
  if (/<(h1|h2|h3|p|ul|ol|li|strong|em|u|blockquote|a|hr|br|div|span)\b/i.test(text)) {
    return <div dangerouslySetInnerHTML={{ __html: clean(text) }} />;
  }
  const lines = text.split('\n');
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      const items = listBuf;
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-6 my-3 space-y-1">
          {items.map((l, i) => (
            <li key={`li-${out.length}-${i}`} dangerouslySetInnerHTML={{ __html: clean(inline(l)) }} />
          ))}
        </ul>
      );
      listBuf = [];
    }
  };
  const inline = (s) => s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
  lines.forEach((line, idx) => {
    if (line.startsWith('# ')) { flushList(); out.push(<h1 key={`h1-${idx}`}>{line.slice(2)}</h1>); }
    else if (line.startsWith('## ')) { flushList(); out.push(<h2 key={`h2-${idx}`}>{line.slice(3)}</h2>); }
    else if (line.startsWith('### ')) { flushList(); out.push(<h3 key={`h3-${idx}`}>{line.slice(4)}</h3>); }
    else if (line.match(/^[-*\d]+\.? /)) {
      const item = line.replace(/^[-*] /, '').replace(/^\d+\. /, '');
      listBuf.push(item);
    }
    else if (line.trim() === '') { flushList(); }
    else { flushList(); out.push(<p key={`p-${idx}`} dangerouslySetInnerHTML={{ __html: clean(inline(line)) }} />); }
  });
  flushList();
  return out;
};

const SLUG_MAP = {
  'terms': 'terms',
  'privacy': 'privacy',
  'dmca': 'dmca',
};

const LegalPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null); setError(false);
    api.get(`/legal/${slug}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <Navigate to="/" replace />;
  if (!data) return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-white/60">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <SEO title={data.title} description={`${data.title} for Jwalek \u2014 our policies and terms of use.`} path={`/legal/${slug}`} />
      <Card className="jw-card p-8 sm:p-10">
        <div className="jw-prose" data-testid={`legal-${slug}-content`}>
          {renderMarkdown(data.content)}
        </div>
        <p className="text-xs mt-8" style={{ color: 'var(--jw-text-subtle)' }}>Last updated: {data.updated_at ? new Date(data.updated_at).toLocaleDateString() : '—'}</p>
      </Card>
    </div>
  );
};

export default LegalPage;
